import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {useHotelRoomsFilters} from 'hooks/useHotelRoomsFilters'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Grid} from '@vio/atlas/helpers/Grid'
import {HorizontalScroll} from '@vio/atlas/helpers/HorizontalScroll'
import {Icon} from '@vio/atlas/Icon'
import {Pill} from '@vio/atlas/Pill'
import {Text} from '@vio/atlas/Text'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {getLanguageCode} from '@vio/core/src/localization/services/locale'
import {
  RoomsFilterKey,
  RoomsFilterParameters
} from '@vio/core/src/room/business/utils/filters'
import {getIsDealFreezeEnabled} from '@vio/shared/src/dealFreeze/modules/selector'
import {shouldShowFreezeIcon} from '@vio/shared/src/dealFreeze/utils'

import {filtersConfig, messages} from './constants'

type Props = {
  availableFilters: RoomsFilterParameters
}

const PositionerWrapper = styled('div')`
  display: flex;
  position: relative;
  &.--mobile {
    width: 100%;
  }
`
const RoomsFiltersTitleWrapper = styled(Text)`
  flex-shrink: 0;
`

export const RoomsFiltersTitle = () => {
  const {isMobile} = useDeviceLayout()
  return (
    <RoomsFiltersTitleWrapper variant={isMobile ? 'titleS' : 'labelM'}>
      <FormattedMessageWrapper id="filterBy" defaultMessage="Filter by" />:
    </RoomsFiltersTitleWrapper>
  )
}

export const RoomsFilters = ({availableFilters}: Props) => {
  const {isMobile} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const language = useSelector(getLanguageCode)
  const isDealFreezeEnabled = useSelector(getIsDealFreezeEnabled)
  const {activeRoomsFilters, toggleRoomsFilter} = useHotelRoomsFilters()
  const onRoomsFilterClick = (id: RoomsFilterKey) => {
    toggleRoomsFilter(id)
  }

  const filters = filtersConfig.filter(filter => availableFilters[filter.id])
  const filtersToShow = filters.filter(filter => {
    if (!isDealFreezeEnabled) {
      return filter.id !== RoomsFilterKey.DealFreeze
    }
    return true
  })

  if (!filtersToShow.length) return null

  return (
    <PositionerWrapper className={cx(isMobile && '--mobile')}>
      <HorizontalScroll columnGap="s300" rowGap="s300">
        <Grid
          container
          spacing="s000"
          direction="row"
          alignItems="center"
          gap={isMobile ? 's300' : 's500'}
          wrap="nowrap"
        >
          <RoomsFiltersTitle />
          <Box display="flex" gap="s300" alignItems="center">
            {filtersToShow.map(filter => (
              <Pill
                key={filter.id}
                isSelected={activeRoomsFilters.includes(filter.id)}
                iconStart={
                  (filter.id !== RoomsFilterKey.DealFreeze ||
                    shouldShowFreezeIcon(language)) && (
                    <Icon name={filter.icon} />
                  )
                }
                iconEnd={
                  activeRoomsFilters.includes(filter.id) && (
                    <Icon name="Close" />
                  )
                }
                onClick={() => onRoomsFilterClick(filter.id)}
                dataId={`roomFilter-${filter.id}`}
              >
                {formatMessage(messages[filter.id])}
              </Pill>
            ))}
          </Box>
        </Grid>
      </HorizontalScroll>
    </PositionerWrapper>
  )
}
